import { z } from 'zod';

import { IataSchema, MiceMoneySchema, NumericIdSchema } from './mice_refs';

// RSVP em modo levantamento e o custo estimado do evento. Folha: só importa `mice_refs` (./mice e
// ./mice_invites importam daqui).
//
// `purchase` (o de sempre): o convite termina na escolha do voo. `survey` (levantamento): o
// convite colhe presença, saída e respostas sem comprar nada; "liberar as compras" troca para
// `purchase`, e quem já respondeu segue do passo das regras, sem refazer a saída nem as
// perguntas. O custo estimado simula as passagens de quem aceitou, pela saída de cada um.

export const MiceRsvpModeSchema = z.enum(['purchase', 'survey']);
export type MiceRsvpMode = z.infer<typeof MiceRsvpModeSchema>;

/** Troca do modo do convite. `survey` só antes de qualquer necessidade ou pedido do evento
 *  (409 `purchases_started`); `purchase` libera as compras a quem respondeu no levantamento. */
export const MiceRsvpModeRequestSchema = z.object({
  organizerId: NumericIdSchema,
  mode: MiceRsvpModeSchema,
});
export type MiceRsvpModeRequest = z.infer<typeof MiceRsvpModeRequestSchema>;

/** O voo mais barato achado num sentido (até 1 parada). */
export const MiceEstimateFareSchema = z.object({
  price: MiceMoneySchema,
  carrier: z.string(),
  departAt: z.string(),
});
export type MiceEstimateFare = z.infer<typeof MiceEstimateFareSchema>;

/** Uma saída (e a volta para ela, ou para `returnTo`): quantos convidados e a tarifa de cada
 *  sentido; `null` = nenhum voo achado (ou o sentido não se aplica). `error`: a busca falhou. */
export const MiceEstimateLineSchema = z.object({
  origin: IataSchema,
  returnTo: IataSchema.nullable(),
  travelers: z.number().int().min(1),
  outbound: MiceEstimateFareSchema.nullable(),
  inbound: MiceEstimateFareSchema.nullable(),
  error: z.enum(['search_failed', 'no_flight']).nullable(),
});
export type MiceEstimateLine = z.infer<typeof MiceEstimateLineSchema>;

export const MiceEstimateStatusSchema = z.enum(['queued', 'running', 'done', 'failed']);
export type MiceEstimateStatus = z.infer<typeof MiceEstimateStatusSchema>;

/**
 * A última estimativa do evento. `total` = Σ (ida + volta) × convidados de cada saída com as duas
 * tarifas; `perTraveler` = total ÷ convidados contados. `travelersCounted`: os que entraram no
 * total; `travelersWithoutOrigin`: aceitaram e ainda não disseram de onde saem;
 * `travelersNoFlight`: não precisam de voo; `travelersUnpriced`: a saída não achou tarifa.
 */
export const MiceEventEstimateSchema = z.object({
  status: MiceEstimateStatusSchema,
  requestedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  lines: z.array(MiceEstimateLineSchema),
  total: MiceMoneySchema.nullable(),
  perTraveler: MiceMoneySchema.nullable(),
  travelersCounted: z.number().int().min(0),
  travelersWithoutOrigin: z.number().int().min(0),
  travelersNoFlight: z.number().int().min(0),
  travelersUnpriced: z.number().int().min(0),
});
export type MiceEventEstimate = z.infer<typeof MiceEventEstimateSchema>;

/** `GET .../estimate`: a última, ou `null` se o evento nunca foi simulado. */
export const MiceEventEstimateReadSchema = z.object({
  estimate: MiceEventEstimateSchema.nullable(),
});
export type MiceEventEstimateRead = z.infer<typeof MiceEventEstimateReadSchema>;
